import {useEffect, useState} from "react";
import {ChartDataModel, ChartModel} from "../models/ChartDataModel";
import {useDashboardContext} from "../dashboardContextProvider";
import {DashboardColors} from "../models/DashboardColors";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {GetStatusString, VejledersamtaleStatusEnum} from "../../vejledning/vejledersamtaler/VejledersamtaleStatusEnum";
import {VejledersamtaleEnum} from "../../vejledning/vejledersamtaler/vejledersamtaleModel";
import {getEnums} from "../../../core/helpers/enumToList";
import {getMinMaxDage, GetVejledningTimeString, VejledningDatoKategoriEnum} from "./VejledningDatoKategoriEnum";
import {CategoryCounts, DashboardAmountEnum, translateDashboardAmountEnum} from "./DashboardAmountEnum";
import {removeWhiteSpace} from "../../../core/helpers/stringHelper";
import {nameof} from "ts-simple-nameof";
import {DashboardListForloebModel} from "../dashboardList/models/DashboardListForloebModel";
import {InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import {useHistory} from "react-router-dom";
import {RoutePaths} from "../../../infrastructure/routes";
const useDashboardVejledersamtaler = () => {
    const {state} = useDashboardContext();
    const history = useHistory();

    const [introduktionPieChart, setIntroduktionPieChart] = useState<ChartModel[]>([]);
    const [justerendePieChart, setJusterendePieChart] = useState<ChartModel[]>([]);
    const [afsluttendePieChart, setAfsluttendePieChart] = useState<ChartModel[]>([]);
    const [vejledersamtalerTidSidenSidste, setVejledersamtalerTidSidenSidste] = useState<ChartModel[]>([]);

    const [introduktionTotalAmount, setIntroduktionTotalAmount] = useState<number>(0);
    const [justerendeTotalAmount, setJusterendeTotalAmount] = useState<number>(0);
    const [afsluttendeTotalAmount, setAfsluttendeTotalAmount] = useState<number>(0);
    const [vejledersamtalerTidSidenSidsteTotalAmount, setVejledersamtalerTidSidenSidsteTotalAmount] = useState<number>(0);

    const [justerendeVejledersamtalerBarChart, setJusterendeVejledersamtalerBarChart] = useState<ChartModel>();
    const [showIkkePlanlagte, setShowIkkePlanlagte] = useState<boolean>(true);

    useEffect(() => {
        const getModelValue = (vejledersamtaleType: VejledersamtaleEnum): string => {
            switch (vejledersamtaleType) {
                case VejledersamtaleEnum.Introduktion:
                    return nameof<DashboardListForloebModel>(x => x.introVejledersamtaleStatus).toLowerCase();
                case VejledersamtaleEnum.Justering:
                    return nameof<DashboardListForloebModel>(x => x.justerendeVejledersamtaleStatus).toLowerCase();
                case VejledersamtaleEnum.Afsluttende:
                    return nameof<DashboardListForloebModel>(x => x.afsluttendeVejledersamtaleStatus).toLowerCase();
                default:
                    return "";
            }
        };
        const handleVejledersamtaleTypeClick = (status: string, vejledersamtaleType: VejledersamtaleEnum) => {
            history.push({
                pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                state: [
                    {
                        initialFilterId: getModelValue(vejledersamtaleType),
                        initialFilterValue: status
                    },
                ] as InitialFilterModel[]
            });
        }

        const endnuIkkePlanlagtStatus = GetStatusString(VejledersamtaleStatusEnum.EndnuIkkePlanlagt);

        const getStatusForType = (forloeb: DashboardListForloebModel, vejledersamtaleType: VejledersamtaleEnum): string => {
            switch (vejledersamtaleType) {
                case VejledersamtaleEnum.Introduktion:
                    return forloeb.introVejledersamtaleStatus;
                case VejledersamtaleEnum.Justering:
                    return forloeb.justerendeVejledersamtaleStatus;
                case VejledersamtaleEnum.Afsluttende:
                    return forloeb.afsluttendeVejledersamtaleStatus;
                default:
                    return "";
            }
        };

        const handleVejledersamtaleTypeByForloeb = (vejledersamtaleType: VejledersamtaleEnum): { pieChart: ChartModel[]; totalAmount: number } => {
            const allStatusEnum = getEnums(VejledersamtaleStatusEnum);
            const statusCounts = {} as { [key: string]: number };

            allStatusEnum.forEach(enumValue => {
                statusCounts[GetStatusString(enumValue)] = 0;
            });

            let totalAmount = 0;
            state.dashboardListData.forEach(forloeb => {
                const status = getStatusForType(forloeb, vejledersamtaleType);

                if (!showIkkePlanlagte && status === endnuIkkePlanlagtStatus)
                    return;

                statusCounts[status] = (statusCounts[status] ?? 0) + 1;
                totalAmount++;
            });

            const pieChart: ChartModel = {
                name: Localizer.global_vejledersamtaler(),
                elements: allStatusEnum.map(enumValue => {
                    const statusName = GetStatusString(enumValue);
                    return new ChartDataModel(
                        enumValue.toString(),
                        statusName,
                        statusCounts[statusName] ?? 0,
                        DashboardColors.GetVejledningColor(statusName),
                        () => handleVejledersamtaleTypeClick(statusName, vejledersamtaleType)
                    );
                })
            };

            return { pieChart: [pieChart], totalAmount };
        };

        const introduktionData = handleVejledersamtaleTypeByForloeb(VejledersamtaleEnum.Introduktion);
        const justerendeData = handleVejledersamtaleTypeByForloeb(VejledersamtaleEnum.Justering);
        const afsluttendeData = handleVejledersamtaleTypeByForloeb(VejledersamtaleEnum.Afsluttende);

        setIntroduktionPieChart(introduktionData.pieChart);
        setJusterendePieChart(justerendeData.pieChart);
        setAfsluttendePieChart(afsluttendeData.pieChart);

        setIntroduktionTotalAmount(introduktionData.totalAmount)
        setJusterendeTotalAmount(justerendeData.totalAmount)
        setAfsluttendeTotalAmount(afsluttendeData.totalAmount)

        const HandleVejledersamtaleTid = (): ChartModel[] => {
            const allDatoKategoriEnums = getEnums(VejledningDatoKategoriEnum);
            const countsByCategory = {
                [VejledningDatoKategoriEnum.UnderEnMaaned]: 0,
                [VejledningDatoKategoriEnum.OverEnMaaned]: 0,
                [VejledningDatoKategoriEnum.OverTreMaaneder]: 0,
                [VejledningDatoKategoriEnum.OverSeksMaaneder]: 0,
                [VejledningDatoKategoriEnum.IkkeAfholdt]: 0,
            } as { [key: number]: number };

            state.dashboardListData.forEach(forloeb => {
                const daysSince = forloeb.dageSidenSidsteVejledersamtale;
                if (daysSince === undefined || daysSince === null)
                    countsByCategory[VejledningDatoKategoriEnum.IkkeAfholdt]++;
                else if (daysSince <= 30)
                    countsByCategory[VejledningDatoKategoriEnum.UnderEnMaaned]++;
                else if (daysSince <= 90)
                    countsByCategory[VejledningDatoKategoriEnum.OverEnMaaned]++;
                else if (daysSince <= 180)
                    countsByCategory[VejledningDatoKategoriEnum.OverTreMaaneder]++;
                else
                    countsByCategory[VejledningDatoKategoriEnum.OverSeksMaaneder]++;
            });

            setVejledersamtalerTidSidenSidsteTotalAmount(state.dashboardListData.length);

            const handleSidsteVejledersamtaleClick = (datoCategory: [number | undefined, number | undefined]) => {
                history.push({
                    pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                    state: [
                        {
                            initialFilterId: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteVejledersamtale).toLowerCase(),
                            initialFilterValue: datoCategory
                        },
                    ] as InitialFilterModel[]
                });
            };

            const pieModel: ChartModel = {
                name: Localizer.global_vejledersamtaler(),
                elements: allDatoKategoriEnums.map(enumValue => new ChartDataModel(
                    enumValue.toString(),
                    GetVejledningTimeString(enumValue),
                    countsByCategory[enumValue],
                    DashboardColors.GetVejledningColor(GetVejledningTimeString(enumValue)),
                    () => handleSidsteVejledersamtaleClick(getMinMaxDage(enumValue))
                ))
            };

            return [pieModel];
        }
        setVejledersamtalerTidSidenSidste(HandleVejledersamtaleTid());

        // Build bar buckets from the same per-forloeb field used in list filtering.
        const justerendeVejledersamtalerCounts: CategoryCounts[] = [];

        const categoryCounts = {
            [DashboardAmountEnum.Nul]: 0,
            [DashboardAmountEnum.EnTilTo]: 0,
            [DashboardAmountEnum.TreTilFire]: 0,
            [DashboardAmountEnum.FemTilSeks]: 0,
            [DashboardAmountEnum.SyvTilTi]: 0,
            [DashboardAmountEnum.OverTi]: 0
        } as { [key: number]: number };

        state.dashboardListData.forEach(forloeb => {
            const count = forloeb.antalJusterendeVejledersamtaler ?? 0;
            if (count === 0)
                categoryCounts[DashboardAmountEnum.Nul]++;
             else if (count <= 2)
                categoryCounts[DashboardAmountEnum.EnTilTo]++;
             else if (count <= 4)
                categoryCounts[DashboardAmountEnum.TreTilFire]++;
             else if (count <= 6)
                categoryCounts[DashboardAmountEnum.FemTilSeks]++;
             else if (count <= 10)
                categoryCounts[DashboardAmountEnum.SyvTilTi]++;
             else if (count > 10)
                categoryCounts[DashboardAmountEnum.OverTi]++;
        });

        const handleAntalJusterendeSamtalerClick = (minMaxValue: [number | undefined, number | undefined]) => {
            history.push({
                pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                state: [
                    {
                        initialFilterId: nameof<DashboardListForloebModel>(x => x.antalJusterendeVejledersamtaler).toLowerCase(),
                        initialFilterValue: minMaxValue
                    },
                ] as InitialFilterModel[]
            });
        }

        getEnums(DashboardAmountEnum).forEach(enumValue => {
            justerendeVejledersamtalerCounts.push({
                amountGroupCategory: enumValue,
                groupCategoryName: translateDashboardAmountEnum(enumValue),
                count: categoryCounts[enumValue],
                onCellClick: () => handleAntalJusterendeSamtalerClick(getMinMaxValue(enumValue)),
            })
        });

        const justerendeVejledersamtaleCountChart = mapToBarChartModel(justerendeVejledersamtalerCounts, Localizer.dashboard_justerendeVejledersamtaleAmount());
        setJusterendeVejledersamtalerBarChart(justerendeVejledersamtaleCountChart);
    }, [state.vejledersamtaler, state.dashboardListData, showIkkePlanlagte, history])

    const mapToBarChartModel = (chartModel: CategoryCounts[], barName: string): ChartModel => {
        let elements: ChartDataModel[] = [];
        chartModel.forEach((chartModel, index) => {
            elements.push(new ChartDataModel(chartModel.amountGroupCategory.toString(), chartModel.groupCategoryName, chartModel.count, DashboardColors.JusterendeVejledersamtaleBarChartColor, chartModel.onCellClick))
        });

        return {
            name: barName,
            elements: elements
        };
    };

    return {
        isLoading: state.vejledersamtalerIsLoading,
        setShowIkkePlanlagte: setShowIkkePlanlagte,
        showIkkePlanlagte: showIkkePlanlagte,

        introduktionPieChart: introduktionPieChart,
        justerendePieChart: justerendePieChart,
        afsluttendePieChart: afsluttendePieChart,
        vejledersamtalerTidSidenSidste: vejledersamtalerTidSidenSidste,

        vejledersamtalerTotalAmount: state.vejledersamtaler.length,
        introduktionTotalAmount: introduktionTotalAmount,
        justerendeTotalAmount: justerendeTotalAmount,
        afsluttendeTotalAmount: afsluttendeTotalAmount,
        vejledersamtalerTidSidenSidsteTotalAmount: vejledersamtalerTidSidenSidsteTotalAmount,

        justerendeVejledersamtalerBarChart: justerendeVejledersamtalerBarChart,
    }
}
export default useDashboardVejledersamtaler;

export const getMinMaxValue = (value: DashboardAmountEnum): [number | undefined, number | undefined] => {
    switch (value) {
        case DashboardAmountEnum.Nul:
            return [0, 0];
        case DashboardAmountEnum.EnTilTo:
            return [1, 2];
        case DashboardAmountEnum.TreTilFire:
            return [3, 4];
        case DashboardAmountEnum.FemTilSeks:
            return [5, 6];
        case DashboardAmountEnum.SyvTilTi:
            return [7, 10];
        case DashboardAmountEnum.OverTi:
            return [10, undefined];
        default:
            return [0, 100];
    }
}
